/* eslint-disable no-param-reassign */
import { produce } from 'immer';
import { remove, indexBy, prop } from 'ramda';
import { matchPath } from 'react-router';

import * as types from 'constants/ActionTypes';
import { ROLES, SEARCH, SELECTED_DEALER_ID, STATUS } from 'constants/FilterConstants';
import { PATH } from 'constants/RoutesConstants';
import { INTEGRATION, USER_STATUS, USER_TYPE } from 'constants/UserConstants';

import * as appointments from 'modules/appointments/actionTypes';
import { FETCH_AUDITS_SUCCESS, FETCH_AUDIT_SUCCESS } from 'modules/audits/actionTypes';
import * as chat from 'modules/chat/actionTypes';
import { PREDEFINED_MESSAGES_FETCH_SUCCESS as FETCH_DEALER_PREDEFINED_MESSAGES_SUCCESS } from 'modules/dealers/actionTypes';
import { FETCH_GUIDELINES_SUCCESS } from 'modules/guidelines/actionTypes';
import * as actionTypes from 'modules/interventions/actionTypes';
import {
  FETCH_PREDEFINED_MESSAGES_SUCCESS,
  FETCH_ALL_BY_DEALER_SUCCESS as FETCH_ALL_BY_DEALER_PREDEFINED_MESSAGES_SUCCESS,
} from 'modules/predefinedMessages/actionTypes';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { SOCKET_CHECK } from 'modules/sockets/constants';

import { CURRENT_USER_DATA_SUCCESS } from 'modules/users/actionTypes';
import { uniq } from 'utils/ObjectUtils';

import { getPersistedFilters } from '../services/StateLoader';

const persistedFilters = getPersistedFilters()?.filters?.users ?? {};

const resetFilters = {
  [STATUS]: [USER_STATUS.ACTIVE],
  [ROLES]: [],
};

/**
 * @typedef {{
 *   ids: string[],
 *   links: Record<string, import("modules/global/generic.types").Link>,
 *   page: import("modules/global/generic.types").Page,
 *   isFetching: boolean,
 *   apiError: string,
 * }} UserAssignedReducer
 */

/**
 * @typedef {{
 *   data: {
 *     content: Record<string, import("modules/users/types/projections/user").User>,
 *     exist: {
 *       isUsed: boolean,
 *       isFetching: boolean,
 *     },
 *     page: import("modules/global/generic.types").Page,
 *     assigned: UserAssignedReducer,
 *   },
 *   apiError: string,
 *   isFetching: boolean,
 *   isValidating: boolean,
 *   filters: {
 *     status: string[],
 *     roles: string[],
 *     text: string,
 *     dealerId: string,
 *     rolesNot: string[],
 *   },
 *   selectedUserId: string,
 *   ui: {
 *     details: {
 *       ready: boolean,
 *     }
 *   }
 * }} LegacyUserReducer
 */

const initialAssigned = {
  ids: [],
  links: {},
  page: {},
  isFetching: false,
  apiError: null,
};

const initialState = {
  data: {
    content: {},
    exist: {
      isFetching: false,
      isUsed: false,
    },
    page: {},
    assigned: initialAssigned,
  },
  apiError: null,
  isFetching: false,
  isValidating: false,
  filters: {
    ...resetFilters,
    ...persistedFilters,
    [SEARCH]: '',
    [SELECTED_DEALER_ID]: null,
    rolesNot: [INTEGRATION],
  },
  selectedUserId: null,
  ui: {
    details: {
      ready: false,
    },
  },
};

const reducer = produce((draft, action) => {
  const { payload, meta } = action;
  switch (action.type) {
    case LOCATION_CHANGE: {
      const isMatching = [PATH.USERS_DETAILED.path, PATH.PROFILE.path].find(
        path => matchPath(path, action.payload.location.pathname) !== null
      );
      if (isMatching) {
        draft.ui.details.ready = false;
      }
      break;
    }
    case types.USER_DETAILS_PAGE_IS_READY:
      draft.ui.details.ready = true;
      break;
    case types.FETCH_DEALERS_SELECT_SUCCESS:
    case types.FETCH_DEALERS_SELECT_REQUEST: {
      if (action.meta.source === 'user-details') {
        draft.isFetching = action.type === types.FETCH_DEALERS_SELECT_REQUEST;
      }
      break;
    }
    case types.FETCH_USERS_REQUEST:
      draft.isFetching = true;
      draft.apiError = null;
      draft.selectedUserId = null;
      break;

    case types.FETCH_USER_REQUEST:
      if (action.meta.source !== SOCKET_CHECK) {
        draft.isFetching = true;
        draft.apiError = null;
        draft.selectedUserId = action.payload.userId;
      }
      break;

    case types.UPDATE_USER_REQUEST:
    case types.CREATE_USER_REQUEST:
      draft.isFetching = true;
      draft.apiError = null;
      break;

    case types.FETCH_USERS_FAILURE:
      draft.isFetching = false;
      draft.apiError = action.payload;
      break;

    case types.FETCH_USER_FAILURE:
    case types.UPDATE_USER_FAILURE:
    case types.CREATE_USER_FAILURE:
      draft.isFetching = false;
      draft.apiError = action.error;
      break;

    case types.FETCH_USER_SUCCESS: {
      const { payload: user } = action;
      const { userGroups } = user;
      const userGroupIds = userGroups?.map(userGroup => userGroup.id);
      draft.isFetching = false;
      draft.data.content[user.id] = {
        ...user,
        userGroupIds,
      };
      break;
    }

    case types.UPDATE_USER_SUCCESS: {
      const { userGroups } = payload;
      const userGroupIds = userGroups?.map(userGroup => userGroup.id);
      draft.isFetching = false;
      draft.data.content[payload.id] = {
        ...payload,
        userGroupIds,
      };
      break;
    }

    case types.CREATE_USER_SUCCESS:
      draft.isFetching = false;
      draft.data.content[action.payload.id] = action.payload;
      draft.selectedUserId = action.payload.id;
      break;

    case types.FETCH_USERS_SUCCESS:
      draft.isFetching = false;
      draft.data.content = {
        ...draft.data.content,
        ...indexBy(prop('id'))(action.payload.content),
      };
      draft.data.page = action.payload.page;
      break;
    case FETCH_AUDITS_SUCCESS: {
      const users = [];
      action.payload.content.forEach(item => users.push(item.createdBy, item.modifiedBy, item.author));
      draft.data.content = {
        ...draft.data.content,
        ...indexBy(prop('id'))(
          users.filter(user => !!user).map(user => ({ ...draft.data.content?.[user.id], ...user }))
        ),
      };
      break;
    }
    case FETCH_AUDIT_SUCCESS: {
      const auditUsers = [action.payload.createdBy, action.payload.modifiedBy, action.payload.author];
      draft.data.content = {
        ...draft.data.content,
        ...indexBy(prop('id'))(
          auditUsers.filter(user => !!user).map(user => ({ ...draft.data.content?.[user.id], ...user }))
        ),
      };
      break;
    }
    case types.SET_USERS_FILTER:
      draft.isFetching = true;
      draft.filters = {
        ...draft.filters,
        ...action.payload,
      };
      draft.data.content = {};
      break;

    case types.INIT_USERS_DASHBOARD:
      draft.data.content = remove(draft.selectedUserId, draft.data.content);
      draft.selectedUserId = '';
      break;

    case appointments.FETCH_APPOINTMENT_SUCCESS:
    case appointments.PUT_SUCCESS:
    case appointments.POST_SUCCESS:
    case appointments.PATCH_STATUS_SUCCESS: {
      const usersCollection = [
        action.payload.createdBy,
        ...(action.payload.events?.map(({ createdBy }) => createdBy) ?? []),
      ];
      draft.data.content = {
        ...draft.data.content,
        ...indexBy(prop('id'))(usersCollection.filter(user => !!user)),
      };
      break;
    }

    case chat.FETCH_CONVERSATIONS_SUCCESS: {
      const usersCollection = action.payload.content
        .flatMap(({ participants = [], latestMessage }) => [
          ...(latestMessage?.createdBy?.type === USER_TYPE.USER ? [latestMessage.createdBy] : []),
          ...participants.map(({ user }) => user),
        ])
        .filter(user => !!user);

      draft.data.content = {
        ...draft.data.content,
        ...usersCollection.reduce(
          (newUsers, user) => ({
            ...newUsers,
            [user.id]: {
              ...(draft.data.content[user.id] ?? {}),
              ...user,
            },
          }),
          {}
        ),
      };
      break;
    }

    case chat.POST_CONVERSATION_SUCCESS:
    case chat.FETCH_CONVERSATION_SUCCESS: {
      const usersCollection =
        action.payload.participants?.map(({ user }) => user).filter(user => !!user) ?? [];

      if (action.payload.latestMessage?.createdBy?.type === USER_TYPE.USER) {
        usersCollection.push(action.payload.latestMessage.createdBy);
      }

      draft.data.content = {
        ...draft.data.content,
        ...usersCollection.reduce(
          (newUsers, user) => ({
            ...newUsers,
            [user.id]: {
              ...(draft.data.content[user.id] ?? {}),
              ...user,
            },
          }),
          {}
        ),
      };
      break;
    }

    case chat.FETCH_MESSAGES_SUCCESS: {
      const usersCollection = action.payload.content.map(({ createdBy }) => createdBy).filter(user => !!user);

      draft.data.content = {
        ...draft.data.content,
        ...usersCollection.reduce(
          (newUsers, user) => ({
            ...newUsers,
            [user.id]: {
              ...(draft.data.content[user.id] ?? {}),
              ...user,
            },
          }),
          {}
        ),
      };
      break;
    }

    case chat.POST_MESSAGE_SUCCESS: {
      const { createdBy: user } = action.payload;
      draft.data.content = {
        ...draft.data.content,
        [user.id]: {
          ...(draft.data.content[user.id] ?? {}),
          ...user,
        },
      };
      break;
    }

    case types.USER_EXISTS_REQUEST:
      draft.isValidating = true;
      draft.data.exist = {
        isUsed: false,
        isFetching: true,
      };
      break;

    case types.USER_EXISTS_SUCCESS:
      draft.data.exist = {
        isUsed: action.payload,
        isFetching: false,
      };
      draft.isValidating = false;
      break;

    case types.USER_EXISTS_FAILURE:
      draft.data.exist = {
        isUsed: false,
        isFetching: false,
      };
      draft.isValidating = false;
      break;

    case types.CLEAN_USER_EXISTS_REQUEST:
      draft.data.exist = {
        isUsed: false,
        isFetching: false,
      };
      draft.isValidating = false;
      break;

    case types.REMOVE_USER_FILTER:
      if (!meta.isArray) {
        const { [payload.field]: _omitted, ...rest } = draft.filters;
        draft.filters = rest;
      }

      draft.filters[payload.field] = draft.filters[payload.field]?.filter(value => value !== payload.value) ?? [];
      draft.data.content = {};
      break;

    case types.RESET_USER_FILTER:
      draft.filters = { ...draft.filters, ...resetFilters };
      draft.data.content = {};
      break;

    case actionTypes.FETCH_INTERVENTIONS_SUCCESS: {
      const newUsers = action.payload?.content?.reduce((acc, { assignedUser, createdBy, serviceAdvisorUser }) => {
        const interventionUsers = [assignedUser, createdBy, serviceAdvisorUser];
        interventionUsers.forEach(user => {
          if (user?.id) {
            acc[user.id] = {
              ...acc[user.id],
              ...user
            };
          }
        });

        return acc;
      }, draft.data.content);

      draft.data.content = newUsers;
      break;
    }
    case types.FETCH_ASSIGNED_USERS:
    case types.FETCH_ASSIGNED_USERS_NEXT_PAGE:
      draft.data.assigned.isFetching = true;
      draft.data.assigned.apiError = null;
      break;

    case types.CLEAN_ASSIGNED_USERS:
      draft.data.assigned = initialAssigned;
      break;

    case types.FETCH_ASSIGNED_USERS_SUCCESS:
      draft.data.content = {
        ...draft.data.content,
        ...payload.content.reduce((acc, u) => ({ ...acc, [u.id]: u }), {}),
      };
      draft.data.assigned.ids = uniq([...draft.data.assigned.ids, ...payload.content.map(({ id }) => id)]);
      draft.data.assigned.links = payload.links.reduce((acc, l) => ({ ...acc, [l.rel]: l }), {});
      draft.data.assigned.isFetching = false;
      draft.data.assigned.apiError = null;
      break;

    case types.FETCH_ASSIGNED_USERS_FAILURE:
      draft.data.assigned.isFetching = false;
      draft.data.assigned.apiError = payload.message;
      break;

    case FETCH_GUIDELINES_SUCCESS:
    case FETCH_PREDEFINED_MESSAGES_SUCCESS:
    case FETCH_ALL_BY_DEALER_PREDEFINED_MESSAGES_SUCCESS:
    case FETCH_DEALER_PREDEFINED_MESSAGES_SUCCESS: {
      const users = action.payload.content
        .flatMap(({ createdBy, modifiedBy }) => [createdBy, modifiedBy])
        .reduce((acc, user) => (!user ? acc : { ...acc, [user?.id]: user }), {});

      draft.data.content = {
        ...draft.data.content,
        ...users,
      };

      break;
    }

    case CURRENT_USER_DATA_SUCCESS:
      draft.data.content[action.payload.id] = action.payload;
      break;

    default:
      break;
  }
}, initialState);

export default reducer;
